import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type {
  CoachingRecommendation,
  LearningAnalytics,
} from '@coffeeplexity/shared';
import { useAppDispatch } from '../../../store';
import { updateCoachingRecommendation } from '../../../store/slices/premiumAnalyticsSlice';

interface EnhancedCoachingViewProps {
  recommendations: CoachingRecommendation[];
  analytics: LearningAnalytics;
}

export const EnhancedCoachingView: React.FC<EnhancedCoachingViewProps> = ({
  recommendations,
  analytics,
}) => {
  const dispatch = useAppDispatch();
  const [selectedRecommendation, setSelectedRecommendation] = useState<
    string | null
  >(null);
  const [filterPriority, setFilterPriority] = useState<
    'all' | 'critical' | 'high' | 'medium' | 'low'
  >('all');

  const handleRecommendationAction = async (
    recommendationId: string,
    action: 'accept' | 'dismiss' | 'defer'
  ) => {
    try {
      await dispatch(
        updateCoachingRecommendation({ recommendationId, action })
      ).unwrap();

      let message = '';
      switch (action) {
        case 'accept':
          message = "Great! We'll track your progress and provide updates.";
          break;
        case 'dismiss':
          message =
            "Understood. We'll adjust future recommendations based on your preferences.";
          break;
        case 'defer':
          message =
            "No problem. We'll remind you about this recommendation later.";
          break;
      }

      Alert.alert('Success', message);
    } catch (error) {
      Alert.alert(
        'Error',
        'Failed to update recommendation. Please try again.'
      );
    }
  };

  const generatePersonalizedInsights = (
    recommendation: CoachingRecommendation
  ) => {
    const insights = [];

    // Based on learning velocity
    if (
      analytics.learningVelocity.currentRate >
      analytics.learningVelocity.averageRate
    ) {
      insights.push({
        type: 'velocity',
        icon: 'trending-up',
        color: '#10B981',
        text: `Your learning velocity is ${Math.round((analytics.learningVelocity.currentRate / analytics.learningVelocity.averageRate - 1) * 100)}% above average - perfect timing for this challenge!`,
      });
    }

    // Based on engagement patterns
    if (analytics.engagementMetrics.motivationLevel > 80) {
      insights.push({
        type: 'motivation',
        icon: 'heart',
        color: '#EF4444',
        text: `High motivation level (${analytics.engagementMetrics.motivationLevel}%) makes this an ideal time to tackle new skills.`,
      });
    }

    // Based on efficiency score
    if (analytics.overallProgress.efficiencyScore > 85) {
      insights.push({
        type: 'efficiency',
        icon: 'speedometer',
        color: '#F59E0B',
        text: `Your excellent efficiency score (${analytics.overallProgress.efficiencyScore}%) suggests you'll master this quickly.`,
      });
    }

    return insights.slice(0, 2); // Return top 2 insights
  };

  const calculateSuccessProbability = (
    recommendation: CoachingRecommendation
  ) => {
    let probability = recommendation.confidence * 100;

    // Adjust based on user's current performance
    if (analytics.engagementMetrics.completionRate > 85) probability += 10;
    if (analytics.learningVelocity.trendDirection === 'accelerating')
      probability += 5;
    if (analytics.overallProgress.efficiencyScore > 80) probability += 5;

    return Math.min(95, Math.round(probability));
  };

  const getImpactVisualization = (recommendation: CoachingRecommendation) => {
    const { expectedImpact } = recommendation;
    return [
      {
        label: 'Skill Growth',
        value: expectedImpact.skillImprovement,
        color: '#6750A4',
      },
      {
        label: 'Efficiency',
        value: expectedImpact.efficiencyGain,
        color: '#10B981',
      },
      {
        label: 'Engagement',
        value: expectedImpact.engagementIncrease,
        color: '#F59E0B',
      },
    ];
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return '#DC2626';
      case 'high':
        return '#EF4444';
      case 'medium':
        return '#F59E0B';
      case 'low':
        return '#10B981';
      default:
        return '#6B7280';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'skill_focus':
        return 'library-outline';
      case 'habit_change':
        return 'refresh-outline';
      case 'schedule_optimization':
        return 'time-outline';
      case 'content_suggestion':
        return 'document-text-outline';
      case 'motivation':
        return 'heart-outline';
      default:
        return 'bulb-outline';
    }
  };

  const filteredRecommendations = recommendations.filter(
    rec => filterPriority === 'all' || rec.priority === filterPriority
  );

  const renderRecommendationCard = (recommendation: CoachingRecommendation) => {
    const isSelected = selectedRecommendation === recommendation.id;
    const insights = generatePersonalizedInsights(recommendation);
    const successProbability = calculateSuccessProbability(recommendation);
    const impactData = getImpactVisualization(recommendation);

    return (
      <View key={recommendation.id} style={styles.recommendationCard}>
        <TouchableOpacity
          style={styles.recommendationHeader}
          onPress={() =>
            setSelectedRecommendation(isSelected ? null : recommendation.id)
          }
        >
          <View style={styles.headerLeft}>
            <View
              style={[
                styles.iconContainer,
                {
                  backgroundColor: `${getPriorityColor(recommendation.priority)}20`,
                },
              ]}
            >
              <Ionicons
                name={getTypeIcon(recommendation.type) as any}
                size={24}
                color={getPriorityColor(recommendation.priority)}
              />
            </View>
            <View style={styles.headerText}>
              <Text style={styles.recommendationTitle}>
                {recommendation.title}
              </Text>
              <View style={styles.metadata}>
                <View
                  style={[
                    styles.priorityBadge,
                    {
                      backgroundColor: getPriorityColor(
                        recommendation.priority
                      ),
                    },
                  ]}
                >
                  <Text style={styles.priorityText}>
                    {recommendation.priority.toUpperCase()}
                  </Text>
                </View>
                <Text style={styles.timeframe}>{recommendation.timeframe}</Text>
                <View style={styles.successProbability}>
                  <Ionicons name='checkmark-circle' size={12} color='#10B981' />
                  <Text style={styles.successText}>
                    {successProbability}% success
                  </Text>
                </View>
              </View>
            </View>
          </View>
          <Ionicons
            name={isSelected ? 'chevron-up' : 'chevron-down'}
            size={20}
            color='#9CA3AF'
          />
        </TouchableOpacity>

        <Text style={styles.description}>{recommendation.description}</Text>

        {/* AI Insights */}
        {insights.length > 0 && (
          <View style={styles.aiInsights}>
            <Text style={styles.insightsTitle}>AI Insights for You</Text>
            {insights.map((insight, index) => (
              <View key={index} style={styles.insightItem}>
                <Ionicons
                  name={insight.icon as any}
                  size={16}
                  color={insight.color}
                />
                <Text style={styles.insightText}>{insight.text}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Impact Visualization */}
        <View style={styles.impactVisualization}>
          <Text style={styles.impactTitle}>Expected Impact</Text>
          <View style={styles.impactBars}>
            {impactData.map((impact, index) => (
              <View key={index} style={styles.impactBar}>
                <Text style={styles.impactLabel}>{impact.label}</Text>
                <View style={styles.impactBarContainer}>
                  <View
                    style={[
                      styles.impactBarFill,
                      {
                        width: `${impact.value}%`,
                        backgroundColor: impact.color,
                      },
                    ]}
                  />
                </View>
                <Text style={styles.impactValue}>+{impact.value}%</Text>
              </View>
            ))}
          </View>
        </View>

        {isSelected && (
          <View style={styles.expandedContent}>
            {/* Detailed Reasoning */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Why This Recommendation?</Text>
              <Text style={styles.reasoning}>{recommendation.reasoning}</Text>
            </View>

            {/* Personalized Action Plan */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>
                Your Personalized Action Plan
              </Text>
              {recommendation.actionSteps.map((step, index) => (
                <View key={step.step} style={styles.actionStep}>
                  <View style={styles.stepHeader}>
                    <View style={styles.stepNumber}>
                      <Text style={styles.stepNumberText}>{step.step}</Text>
                    </View>
                    <View style={styles.stepInfo}>
                      <Text style={styles.stepDescription}>
                        {step.description}
                      </Text>
                      <View style={styles.stepMetadata}>
                        <View style={styles.stepTime}>
                          <Ionicons name='time' size={12} color='#6B7280' />
                          <Text style={styles.stepTimeText}>
                            {step.estimatedTime} min
                          </Text>
                        </View>
                        <View style={styles.stepDifficulty}>
                          <Ionicons
                            name='speedometer'
                            size={12}
                            color='#6B7280'
                          />
                          <Text style={styles.stepDifficultyText}>
                            {step.difficulty}
                          </Text>
                        </View>
                      </View>
                    </View>
                  </View>

                  {step.resources.length > 0 && (
                    <View style={styles.stepResources}>
                      <Text style={styles.resourcesTitle}>Resources:</Text>
                      {step.resources.map((resource, resourceIndex) => (
                        <TouchableOpacity
                          key={resourceIndex}
                          style={styles.resourceItem}
                        >
                          <Ionicons
                            name='document-text'
                            size={14}
                            color='#6750A4'
                          />
                          <Text style={styles.resourceText}>{resource}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  )}

                  {step.successCriteria.length > 0 && (
                    <View style={styles.successCriteria}>
                      <Text style={styles.criteriaTitle}>
                        Success Criteria:
                      </Text>
                      {step.successCriteria.map((criteria, criteriaIndex) => (
                        <View key={criteriaIndex} style={styles.criteriaItem}>
                          <Ionicons
                            name='checkmark'
                            size={12}
                            color='#10B981'
                          />
                          <Text style={styles.criteriaText}>{criteria}</Text>
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              ))}
            </View>

            {/* Progress Tracking */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Progress Tracking</Text>
              <View style={styles.trackingInfo}>
                <View style={styles.trackingItem}>
                  <Ionicons name='calendar' size={16} color='#6750A4' />
                  <Text style={styles.trackingText}>
                    Follow-up:{' '}
                    {new Date(recommendation.followUpDate).toLocaleDateString()}
                  </Text>
                </View>
                <View style={styles.trackingItem}>
                  <Ionicons name='analytics' size={16} color='#6750A4' />
                  <Text style={styles.trackingText}>
                    Results expected in{' '}
                    {recommendation.expectedImpact.timeToSeeResults} days
                  </Text>
                </View>
              </View>
            </View>

            {/* Success Metrics */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>How We'll Measure Success</Text>
              <View style={styles.metricsList}>
                {recommendation.successMetrics.map((metric, index) => (
                  <View key={index} style={styles.metricItem}>
                    <Ionicons name='target' size={14} color='#F59E0B' />
                    <Text style={styles.metricText}>{metric}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        )}

        {/* Action Buttons */}
        <View style={styles.actionButtons}>
          <TouchableOpacity
            style={[styles.actionButton, styles.acceptButton]}
            onPress={() =>
              handleRecommendationAction(recommendation.id, 'accept')
            }
          >
            <Ionicons name='checkmark' size={16} color='#FFFFFF' />
            <Text style={styles.acceptButtonText}>Accept Challenge</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionButton, styles.deferButton]}
            onPress={() =>
              handleRecommendationAction(recommendation.id, 'defer')
            }
          >
            <Ionicons name='time' size={16} color='#F59E0B' />
            <Text style={styles.deferButtonText}>Maybe Later</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionButton, styles.dismissButton]}
            onPress={() =>
              handleRecommendationAction(recommendation.id, 'dismiss')
            }
          >
            <Ionicons name='close' size={16} color='#EF4444' />
            <Text style={styles.dismissButtonText}>Not Interested</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      <View style={styles.header}>
        <Text style={styles.title}>AI Coaching Recommendations</Text>
        <Text style={styles.subtitle}>
          Personalized suggestions based on your learning patterns and goals
        </Text>
      </View>

      {/* Priority Filter */}
      <View style={styles.filterContainer}>
        <Text style={styles.filterLabel}>Filter by Priority:</Text>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.filterButtons}
        >
          {[
            { id: 'all', title: 'All', count: recommendations.length },
            {
              id: 'critical',
              title: 'Critical',
              count: recommendations.filter(r => r.priority === 'critical')
                .length,
            },
            {
              id: 'high',
              title: 'High',
              count: recommendations.filter(r => r.priority === 'high').length,
            },
            {
              id: 'medium',
              title: 'Medium',
              count: recommendations.filter(r => r.priority === 'medium')
                .length,
            },
            {
              id: 'low',
              title: 'Low',
              count: recommendations.filter(r => r.priority === 'low').length,
            },
          ].map(filter => (
            <TouchableOpacity
              key={filter.id}
              style={[
                styles.filterButton,
                filterPriority === filter.id && styles.activeFilterButton,
              ]}
              onPress={() => setFilterPriority(filter.id as any)}
            >
              <Text
                style={[
                  styles.filterButtonText,
                  filterPriority === filter.id && styles.activeFilterButtonText,
                ]}
              >
                {filter.title} ({filter.count})
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Recommendations List */}
      <View style={styles.recommendationsList}>
        {filteredRecommendations.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name='school-outline' size={64} color='#ccc' />
            <Text style={styles.emptyStateText}>
              No recommendations available
            </Text>
            <Text style={styles.emptyStateSubtext}>
              {filterPriority === 'all'
                ? 'Keep learning to receive personalized coaching suggestions'
                : `No ${filterPriority} priority recommendations at this time`}
            </Text>
          </View>
        ) : (
          filteredRecommendations
            .sort((a, b) => {
              const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
              return (
                priorityOrder[b.priority as keyof typeof priorityOrder] -
                priorityOrder[a.priority as keyof typeof priorityOrder]
              );
            })
            .map(renderRecommendationCard)
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  header: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  filterContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingVertical: 12,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
    paddingHorizontal: 20,
  },
  filterButtons: {
    paddingHorizontal: 20,
    gap: 8,
  },
  filterButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  activeFilterButton: {
    backgroundColor: '#6750A4',
    borderColor: '#6750A4',
  },
  filterButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#6750A4',
  },
  activeFilterButtonText: {
    color: '#FFFFFF',
  },
  recommendationsList: {
    padding: 20,
    gap: 16,
  },
  recommendationCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  recommendationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flex: 1,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  headerText: {
    flex: 1,
  },
  recommendationTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 6,
    lineHeight: 20,
  },
  metadata: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  priorityBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  priorityText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  timeframe: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
  },
  successProbability: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  successText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#10B981',
  },
  description: {
    fontSize: 14,
    color: '#4B5563',
    lineHeight: 20,
    marginBottom: 16,
  },
  aiInsights: {
    backgroundColor: '#F0F9FF',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  insightsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0369A1',
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  insightItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 6,
  },
  insightText: {
    fontSize: 12,
    color: '#0369A1',
    flex: 1,
    lineHeight: 16,
  },
  impactVisualization: {
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  impactTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 12,
  },
  impactBars: {
    gap: 8,
  },
  impactBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  impactLabel: {
    fontSize: 12,
    color: '#6B7280',
    minWidth: 70,
  },
  impactBarContainer: {
    flex: 1,
    height: 6,
    backgroundColor: '#E5E7EB',
    borderRadius: 3,
    overflow: 'hidden',
  },
  impactBarFill: {
    height: '100%',
    borderRadius: 3,
  },
  impactValue: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1F2937',
    minWidth: 35,
    textAlign: 'right',
  },
  expandedContent: {
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    paddingTop: 16,
    marginBottom: 16,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 12,
  },
  reasoning: {
    fontSize: 14,
    color: '#4B5563',
    lineHeight: 20,
  },
  actionStep: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  stepHeader: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  stepNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#6750A4',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  stepNumberText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  stepInfo: {
    flex: 1,
  },
  stepDescription: {
    fontSize: 14,
    color: '#1F2937',
    lineHeight: 18,
    marginBottom: 6,
  },
  stepMetadata: {
    flexDirection: 'row',
    gap: 12,
  },
  stepTime: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  stepTimeText: {
    fontSize: 12,
    color: '#6B7280',
  },
  stepDifficulty: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  stepDifficultyText: {
    fontSize: 12,
    color: '#6B7280',
  },
  stepResources: {
    backgroundColor: '#F8F9FA',
    borderRadius: 6,
    padding: 8,
    marginTop: 8,
  },
  resourcesTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 6,
  },
  resourceItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  resourceText: {
    fontSize: 12,
    color: '#6750A4',
    flex: 1,
  },
  successCriteria: {
    backgroundColor: '#F0FDF4',
    borderRadius: 6,
    padding: 8,
    marginTop: 8,
  },
  criteriaTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#166534',
    marginBottom: 6,
  },
  criteriaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 2,
  },
  criteriaText: {
    fontSize: 12,
    color: '#166534',
    flex: 1,
  },
  trackingInfo: {
    gap: 8,
  },
  trackingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  trackingText: {
    fontSize: 14,
    color: '#4B5563',
  },
  metricsList: {
    gap: 6,
  },
  metricItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  metricText: {
    fontSize: 14,
    color: '#4B5563',
    flex: 1,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
    gap: 4,
  },
  acceptButton: {
    backgroundColor: '#10B981',
  },
  acceptButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  deferButton: {
    backgroundColor: '#FEF3C7',
    borderWidth: 1,
    borderColor: '#F59E0B',
  },
  deferButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#F59E0B',
  },
  dismissButton: {
    backgroundColor: '#FEE2E2',
    borderWidth: 1,
    borderColor: '#EF4444',
  },
  dismissButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#EF4444',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyStateText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginTop: 16,
    textAlign: 'center',
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#666',
    marginTop: 8,
    textAlign: 'center',
    lineHeight: 20,
  },
});
